import React, { FC, useEffect, useMemo, useState } from 'react';
import { CardContainer } from '../../layout';
import {
  Button,
  Spinner,
  Table,
  Modal,
  TextInput,
  Textarea,
  Select,
} from 'flowbite-react';
import { useGlobal } from '../../hooks/useGlobal';
import { canView } from '../../utils/permissions';
import { Navigate } from 'react-router-dom';
import {
  getDeviceRedirectGroups,
  addDeviceRedirectGroup,
  updateDeviceRedirectGroup,
  deleteDeviceRedirectGroup,
  getDevices,
  automaticDistribution,
} from '../../services';
import { HiPencil, HiTrash } from 'react-icons/hi';
import { CopyToClipboard } from '../../components';
import { sites } from '../../components/GetSite';

export const DeviceRedirectGroupMain: FC = () => {
  const { user, showAlert } = useGlobal();

  const [loading, setLoading] = useState<boolean>(true);
  const [deviceRedirectGroups, setDeviceRedirectGroups] = useState<any[]>([]);

  // Modal states
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [editGroup, setEditGroup] = useState<string>('');

  // Automatic distribution loading state
  const [autoDistributionLoading, setAutoDistributionLoading] =
    useState<boolean>(false);

  // Form payload
  const initialGroupPayload = {
    name: '',
    defaultMessage: '',
    defaultRedirectUrl: '',
    devices: [] as string[],
    siteId: '',
  };

  const [groupPayload, setGroupPayload] = useState(initialGroupPayload);

  // Devices for selection
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState<boolean>(false);

  // get page actions from user permissions
  const pageAction = useMemo(() => {
    return user?.permissions?.pages?.deviceRedirectGroups?.actions;
  }, [user]);

  // Filter devices based on selected site in modal
  const filteredDevices = useMemo(() => {
    if (!groupPayload.siteId) {
      return [];
    }
    return devices.filter(
      (device) => device.siteId?.toString() === groupPayload.siteId,
    );
  }, [devices, groupPayload.siteId]);

  const fetchDeviceRedirectGroups = async () => {
    try {
      const response: any = await getDeviceRedirectGroups();
      setDeviceRedirectGroups(response?.data || []);
    } catch (error) {
      console.error('fetchDeviceRedirectGroups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    setDevicesLoading(true);
    try {
      const response: any = await getDevices({
        deviceType: 'BOT',
      });
      setDevices(response?.data || []);
    } catch (error) {
      console.error('fetchDevices:', error);
    } finally {
      setDevicesLoading(false);
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditGroup('');
    setGroupPayload(initialGroupPayload);
  };

  const handleFormSubmit = async () => {
    setModalLoading(true);
    try {
      if (editGroup) {
        await updateDeviceRedirectGroup(editGroup, groupPayload);
      } else {
        await addDeviceRedirectGroup(groupPayload);
      }

      await showAlert({
        text: 'İşlem başarılı',
        icon: 'success',
      });

      await fetchDeviceRedirectGroups();
      closeModal();
    } catch (error: any) {
      showAlert({
        text: error?.data?.error || 'İşlem sırasında bir hata oluştu',
        icon: 'warning',
      });
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = async (id: string) => {
    const group = deviceRedirectGroups.find((g) => g._id === id);
    if (group) {
      setGroupPayload({
        name: group.name || '',
        defaultMessage: group.defaultMessage || '',
        defaultRedirectUrl: group.defaultRedirectUrl || '',
        devices: group.devices?.map((d: any) => d._id || d) || [],
        siteId: group.siteId?.toString() || '',
      });
      setEditGroup(id);
      setOpenModal(true);
    }
  };

  const handleDelete = async (id: string) => {
    const result = await showAlert({
      text: 'Silmek istediğinize emin misiniz?',
      icon: 'question',
      showCancelButton: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteDeviceRedirectGroup(id);
        await fetchDeviceRedirectGroups();
        showAlert({
          text: 'Grup başarıyla silindi',
          icon: 'success',
        });
      } catch (error) {
        console.error('Error deleting group:', error);
      }
    }
  };

  const handleAutomaticDistribution = async () => {
    try {
      const confirmAlert = await showAlert({
        text: 'Otomatik dağıtım yapılacak, onaylıyor musunuz?',
        icon: 'question',
        showCancelButton: true,
      });

      if (confirmAlert.isConfirmed) {
        setAutoDistributionLoading(true);
        try {
          await automaticDistribution();

          await showAlert({
            text: 'Otomatik dağıtım başarıyla tamamlandı',
            icon: 'success',
          });

          location.reload();
        } catch (error: any) {
          console.error('handleAutomaticDistribution:', error);
          showAlert({
            text:
              error?.data?.error ||
              'Otomatik dağıtım sırasında bir hata oluştu',
            icon: 'error',
          });
        } finally {
          setAutoDistributionLoading(false);
        }
      }
    } catch (error) {
      console.error('handleAutomaticDistribution:', error);
      setAutoDistributionLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviceRedirectGroups();
    fetchDevices();
  }, []);

  if (!canView(user, 'deviceRedirectGroups')) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <CardContainer
        title="Cihaz Yönlendirme Grupları"
        action={() =>
          pageAction?.automaticDistribution && (
            <div className="flex gap-2 items-center">
              <Button
                size="sm"
                onClick={handleAutomaticDistribution}
                isProcessing={autoDistributionLoading}
                disabled={autoDistributionLoading}
              >
                Otomatik Dağıtım
              </Button>
              <Button size="sm" onClick={() => setOpenModal(true)}>
                Yeni Ekle
              </Button>
            </div>
          )
        }
      >
        {loading ? (
          <div className="text-center">
            <Spinner />
          </div>
        ) : (
          <Table striped hoverable>
            <Table.Head>
              <Table.HeadCell>Grup Adı</Table.HeadCell>
              <Table.HeadCell>Cihazlar</Table.HeadCell>
              <Table.HeadCell>URL</Table.HeadCell>
              <Table.HeadCell></Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {deviceRedirectGroups?.map((item: any) => {
                const { _id: id, name, redirectCode, devices } = item;
                return (
                  <Table.Row key={id}>
                    <Table.Cell>{name}</Table.Cell>
                    <Table.Cell>{devices?.length || 0}</Table.Cell>
                    <Table.Cell>
                      <CopyToClipboard
                        text={`http://wp-link.com/${redirectCode}`}
                      />
                    </Table.Cell>
                    <Table.Cell className="flex justify-end">
                      <Button.Group>
                        <Button
                          size="sm"
                          color="gray"
                          onClick={() => handleEdit(id)}
                        >
                          <HiPencil />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDelete(id)}
                          color="gray"
                        >
                          <HiTrash />
                        </Button>
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </CardContainer>

      {/* Modal */}
      <Modal show={openModal} onClose={closeModal}>
        <Modal.Header>
          {editGroup ? 'Grup Düzenle' : 'Yeni Grup Ekle'}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Grup Adı</label>
              <TextInput
                placeholder="Grup Adı"
                value={groupPayload.name}
                onChange={(e) =>
                  setGroupPayload((p) => ({ ...p, name: e.target.value }))
                }
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Site</label>
              <Select
                value={groupPayload.siteId}
                onChange={(e) =>
                  setGroupPayload((p) => ({
                    ...p,
                    siteId: e.target.value,
                    devices: [], // Clear selected devices when site changes
                  }))
                }
                disabled={!!editGroup}
                required
              >
                <option value="" disabled>
                  Site Seçiniz
                </option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id.toString()}>
                    {site.name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Varsayılan Mesaj
              </label>
              <Textarea
                placeholder="Varsayılan Mesaj"
                value={groupPayload.defaultMessage}
                onChange={(e) =>
                  setGroupPayload((p) => ({
                    ...p,
                    defaultMessage: e.target.value,
                  }))
                }
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Varsayılan Yönlendirme URL
              </label>
              <TextInput
                placeholder="https://example.com"
                value={groupPayload.defaultRedirectUrl}
                onChange={(e) =>
                  setGroupPayload((p) => ({
                    ...p,
                    defaultRedirectUrl: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Cihazlar</label>
              {devicesLoading ? (
                <div className="text-center py-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto border rounded p-3 bg-gray-50">
                  {!groupPayload.siteId ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Cihazları görmek için site seçiniz
                    </p>
                  ) : filteredDevices.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Bu site için cihaz bulunmuyor
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {filteredDevices.map((device) => (
                        <label
                          key={device._id}
                          className={`flex items-center space-x-2`}
                        >
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={groupPayload.devices.includes(device._id)}
                            onChange={(e) => {
                              if (device.clientStatus !== 'CONNECTED') return;

                              if (e.target.checked) {
                                setGroupPayload((p) => ({
                                  ...p,
                                  devices: [...p.devices, device._id],
                                }));
                              } else {
                                setGroupPayload((p) => ({
                                  ...p,
                                  devices: p.devices.filter(
                                    (id) => id !== device._id,
                                  ),
                                }));
                              }
                            }}
                          />
                          <span className={`text-sm `}>
                            {device.name}
                            {device.clientStatus === 'CONNECTED'
                              ? ' - Connected'
                              : ''}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleFormSubmit}
            isProcessing={modalLoading}
            disabled={modalLoading}
          >
            Kaydet
          </Button>
          <Button color="gray" onClick={closeModal}>
            İptal
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
